import React, { useState } from "react";

import {
  Autocomplete,
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { Audience } from "@/types";
import { abbreviateNumber, reformatDate } from "@/utils";
import { Place } from "@mui/icons-material";

interface SelectAudienceDialogProps {
  open: boolean;
  audiences: Audience[];
  onClose: () => void;
  onConfirm: (audiences: Audience[]) => void;
  maxAudiences?: number;
}

const SelectAudienceDialog: React.FC<SelectAudienceDialogProps> = ({
  open,
  audiences,
  onClose,
  onConfirm,
  maxAudiences,
}) => {
  const [selected, setSelected] = useState<Audience[]>([]);

  const handleConfirm = () => {
    onConfirm(selected);
    setSelected([]);
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth aria-labelledby="select-dialog">
      <DialogTitle>
        <Typography>Select Audiences</Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" gutterBottom>
            Select one or more audiences to add as segments to your Activation
          </Typography>

          <Autocomplete
            multiple={true}
            disableCloseOnSelect
            options={audiences}
            getOptionDisabled={(options) =>
              !!(maxAudiences && selected.length >= maxAudiences)
            }
            getOptionLabel={(option) => option.audience_name}
            value={selected}
            onChange={(event, newValue) => setSelected(newValue)}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <MenuItem key={option.audience_id} {...optionProps}>
                  <ListItemText>{option.audience_name}</ListItemText>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {reformatDate(option.created_at)}
                    </Typography>
                    {option.audience_size && (
                      <Stack direction="row" spacing={0.5}>
                        <Place fontSize="inherit" color="disabled" />
                        {option.audience_size[0].num_postcodes && (
                          <Typography variant="body2" color="textSecondary">
                            {abbreviateNumber(
                              option.audience_size[0].num_postcodes,
                            )}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Box>
                </MenuItem>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Audience"
                variant="outlined"
                color="secondary"
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={selected.length === 0}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectAudienceDialog;
